import { IconButton } from 'components/Buttons';
import _, { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import ReactPaginate from 'react-paginate';

export function LayoutBlockPagination({
    itemOffset,
    currentItemsLength,
    totalItemsLength,
    pageCount,
    handlePageClick,
    itemsPerPage,
    columnsPerRow,
    setItemOffset,
    rows,
    setSearchTerm,
    searchTerm,
    readOnly,
    setSelectedSearchBlock,
    areaBlocks,
}) {
    return (
        <div className="d-flex flex-row-reverse align-items-center justify-content-between">
            <div className="d-flex align-items-center">
                <div className="small text-secondary mr-2">
                    {itemOffset + 1} - {itemOffset + currentItemsLength} van {totalItemsLength} onderdelen
                </div>

                <ReactPaginate
                    pageCount={pageCount}
                    forcePage={itemOffset > 0 ? itemOffset / itemsPerPage : 0}
                    onPageChange={handlePageClick}
                    previousLabel="‹"
                    nextLabel="›"
                    containerClassName="pagination mb-0"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    nextClassName="page-item"
                    previousLinkClassName="page-link"
                    nextLinkClassName="page-link"
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    activeClassName="active"
                />
            </div>

            {!readOnly && (
                <>
                    <LayoutBlockSearch
                        rows={rows}
                        setSearchTerm={setSearchTerm}
                        setSelectedSearchBlock={setSelectedSearchBlock}
                        setItemOffset={setItemOffset}
                        itemsPerPage={itemsPerPage}
                        searchTerm={searchTerm}
                        areaBlocks={areaBlocks}
                    />
                </>
            )}
        </div>
    );
}

function LayoutBlockSearch({
    rows,
    setSearchTerm,
    setSelectedSearchBlock,
    itemsPerPage,
    setItemOffset,
    searchTerm,
    areaBlocks = [],
}) {
    const [searchResults, setSearchResults] = useState([]);
    const [searchResultIndex, setSearchResultIndex] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const [matchingBlockIndexes, setMatchingBlockIndexes] = useState([]);

    const blockPositions = useMemo(() => {
        const positions = [];

        rows?.forEach((row, rowIndex) => {
            row.forEach((column, columnIndex) => {
                column.forEach((item) => {
                    if (item.id) {
                        positions.push({
                            block: areaBlocks?.find((block) => block.key === item.id),
                            rowIndex,
                            columnIndex,
                            page: Math.floor(rowIndex / itemsPerPage),
                        });
                    }
                });
            });
        });

        return positions;
    }, [rows, areaBlocks]);

    const handleChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const debouncedResults = useMemo(() => {
        return debounce(handleChange, 300);
    }, []);

    useEffect(() => {
        return () => {
            debouncedResults.cancel();
        };
    });

    useEffect(() => {
        if (searchTerm !== inputValue) {
            handleSearch(searchTerm);
        }
    }, [searchTerm, inputValue]);

    const handleSearch = (input) => {
        const trimmedInputValue = input.trim();

        if (_.isEmpty(trimmedInputValue)) {
            handleClearSearch();
            return;
        }

        const searchValue = trimmedInputValue.toLowerCase();
        let results = [];
        let indexes = [];

        blockPositions.forEach((position, index) => {
            const latestContent = position.block?.latestContent ?? '';
            if (latestContent.toLowerCase().includes(searchValue)) {
                results.push(position.block);
                indexes.push(index);
            }
        });

        if (results.length > 0) {
            const firstOffset = blockPositions[indexes[0]].page * itemsPerPage;
            setItemOffset(firstOffset);
            setMatchingBlockIndexes(indexes);
            setSearchResults(results);
            setSelectedSearchBlock(results[0]);
        } else {
            setMatchingBlockIndexes([]);
            setSearchResults([]);
            setSelectedSearchBlock(undefined);
        }

        setSearchResultIndex(0);
        setInputValue(input);
    };
    const handleNextResult = () => {
        if (matchingBlockIndexes.length === 1) {
            const position = blockPositions[matchingBlockIndexes[0]];
            if (position) {
                const newOffset = position.page * itemsPerPage;
                setItemOffset(newOffset);
                setSelectedSearchBlock(searchResults[0]);
            }
            return;
        }

        const nextIndex = searchResultIndex + 1;
        if (nextIndex >= matchingBlockIndexes.length) {
            return;
        }

        const newMatchingBlockIndex = matchingBlockIndexes[nextIndex];

        const position = blockPositions[newMatchingBlockIndex];

        if (!position) {
            return;
        }

        const newOffset = position.page * itemsPerPage;
        setItemOffset(newOffset);
        setSearchResultIndex(nextIndex);
        setSelectedSearchBlock(searchResults[nextIndex]);
    };

    const handlePreviousResult = () => {
        if (searchResultIndex <= 0) {
            return;
        }

        const prevIndex = searchResultIndex - 1;

        const newMatchingBlockIndex = matchingBlockIndexes[prevIndex];

        const position = blockPositions[newMatchingBlockIndex];
        if (!position) {
            return;
        }

        const newOffset = position.page * itemsPerPage;

        setItemOffset(newOffset);
        setSearchResultIndex(prevIndex);
        setSelectedSearchBlock(searchResults[prevIndex]);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setInputValue('');
        setSearchResultIndex(0);
        setSearchResults([]);
        setMatchingBlockIndexes([]);
        setSelectedSearchBlock(undefined);
    };

    return (
        <div className="search-container">
            <div className="d-flex align-items-center">
                <input
                    type="search"
                    onChange={debouncedResults}
                    placeholder="Zoeken in tabel layout"
                    className="form-control"
                    style={{ height: '38px' }}
                />

                {searchResults.length > 0 && (
                    <>
                        <div className="text-muted small ml-2 mr-2">
                            {searchResultIndex + 1}/{searchResults.length}
                        </div>

                        <IconButton
                            className="text-secondary mr-2"
                            onClick={handlePreviousResult}
                            tooltip="Vorige"
                            icon={<ChevronUp size={18} />}
                        />

                        <IconButton
                            className="text-secondary"
                            onClick={handleNextResult}
                            tooltip="Volgende"
                            icon={<ChevronDown size={18} />}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
